import React, { useContext, useEffect } from 'react';
import { PromiseState } from 'react-refetch';
import interceptConnect from 'helpers/interceptConnect';
import stringToSlug from 'helpers/stringToSlug';
import { EntityContext, EntityTypes } from 'context/entityContext';
import useCombinedRefetchState from 'hooks/useCombinedRefetchState';
import mapKPIData from '../mappings/mapKPIData';
import { ReactComponent as PlaylistsIcon } from 'icons/topics/playlist.svg';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import KPIWrapper, { PlaylistKPIWrapper } from '../KPIWrapper/KPIWrapper';
import { TopicFilterNames } from 'components/TopicFilters/shared';
import { stringify } from 'querystring';
import { Container } from './shared.style';
import PlaylistCompareKPI from './PlaylistCompareKPIs';
import PlaylistKPIList from './PlaylistKPIList';
import useMedia from 'hooks/useMedia';
import { IToolbarItemType } from 'components/utility/GenericComponent/GenericComponent';

interface IPlaylistKPIsProps {
  fetchPlaylistData: any;
  playlistData: PromiseState<any>;
}

const dataSources = (): any => {
  return {
    fetchPlaylistData: (
      countryIds: string[],
      entityIds: string[],
      entityType: EntityTypes,
      customerFilter: TopicFilterNames[],
      region: string,
      customRegion: string | string[],
      compareMode?: string
    ): any => {
      const params = stringify({
        entityIds,
        entityType,
        customerFilter,
        countryIds,
        region,
        customRegion,
      });
      return {
        playlistData: {
          url: `playlistkpi?${params}`,
          interceptionData: {
            customerFilter,
            countryIds,
            entityIds,
            entityType,
            region,
            customRegion,
            compareMode,
          },
        },
      };
    },
  };
};

const PlaylistKPIs: React.FC<IPlaylistKPIsProps> = (props) => {
  const { fetchPlaylistData, playlistData = PromiseState.create() } = props;
  const ctx = useContext(EntityContext);
  const isLargeScreen = useMedia('(min-width: 50em)');
  const {
    compareMode,
    countryIds,
    entityIds,
    entityType,
    filters,
    dataColors,
    customRegionFilter,
  } = ctx;

  const compareArray = compareMode === 'country' ? countryIds : entityIds;
  const playlistExportUrl = playlistData?.value?.exportUrl;
  const errorCause = playlistData?.value?.reason;

  useEffect(() => {
    fetchPlaylistData(
      compareMode === 'country' ? countryIds : filters?.country,
      entityIds,
      entityType,
      filters?.streaming,
      filters?.region,
      customRegionFilter,
      compareMode
    );
  }, [
    compareMode,
    countryIds,
    entityIds,
    entityType,
    fetchPlaylistData,
    filters,
    customRegionFilter,
  ]);

  const [, , , reason] = useCombinedRefetchState([playlistData]);

  const mappedPlaylistData = mapKPIData({
    data: playlistData?.value?.kpiData,
    compareMode,
    compareArray,
  });

  return (
    <Container
      compareMode={compareMode}
      isMobileView={!isLargeScreen}
      data-pw={stringToSlug('Playlist KPIs')}
    >
      <ErrorBoundary
        error={
          errorCause?.error || reason
            ? new Error(errorCause?.error || reason)
            : null
        }
        errorInfo={errorCause?.errorInfo}
      >
        <KPIWrapper
          allowedFilters={['country', 'streaming', 'region', 'customRegion']}
          title={`${compareMode ? 'Compare ' : ''}Playlist KPIs`}
          glossarySlug="kpis/playlist-kpis"
          icon={PlaylistsIcon}
          extraToolbarItems={[
            {
              label: 'Export Playlist KPI data',
              href: playlistExportUrl,
              type: IToolbarItemType.Export,
              order: 1,
            },
          ]}
          newDesign
          titleAlignLeft="0"
          filterMarginLeft="3.1em"
        >
          <PlaylistKPIWrapper>
            {!compareMode && (
              <PlaylistKPIList
                isMobileView={!isLargeScreen}
                mappedPlaylistData={mappedPlaylistData}
                error={errorCause?.error}
              />
            )}

            {compareMode && (
              <PlaylistCompareKPI
                mappedPlaylistData={mappedPlaylistData}
                compareMode={compareMode}
                dataColors={dataColors}
                isMobileView={!isLargeScreen}
              />
            )}
          </PlaylistKPIWrapper>
        </KPIWrapper>
      </ErrorBoundary>
    </Container>
  );
};

export default interceptConnect(dataSources as any)(PlaylistKPIs as any);
